import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseCircleOutlined, PlusCircleOutlined, EditOutlined } from '@ant-design/icons';

import './style.css';
const MultiInput = ({
  value,
  icon,
  placeholder,
  type,
  style,
  onChange,
  disabled,
  onBlur,
  maxLength,
}) => {
  const { t } = useTranslation();

  const [values, setValues] = useState(value ?? []);
  const [finalValue, setFinalValue] = useState('');
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingValue, setEditingValue] = useState('');

  const [disallowedNames, setDisallowedNames] = useState([
    t('new_category'),
    t('new_property'),
    t('new_plant'),
    t('new_area'),
    t('new_sub_area'),
    t('new_line'),
    t('new_machine'),
    t('new_assembly'),
    t('new_equipment'),
    t('new_component'),
    t('new_instrument'),
    t('new_system'),
    t('new_cost_center'),
    t('new_manufacturer'),
    t('new_organization'),
    t('new_department'),
    t('new_interconnection'),
    t('new_user'),
    t('new_user_group'),
    t('new_executor'),
    'email@email.com',
  ]);

  const addOption = () => {
    if (
      finalValue &&
      !disallowedNames.some(
        (name) =>
          name.toLocaleLowerCase().replace(/ /g, '') ===
          `${finalValue}`?.toLocaleLowerCase().replace(/ /g, ''),
      ) &&
      !values.includes(finalValue) &&
      editingIndex === -1
    ) {
      const newValue =
        type === 'number'
          ? values.concat([finalValue]).map((v) => v.replace(/[^\d\.\,]*/g, '').replace(/\,/g, '.'))
          : values.concat([finalValue]);

      onChange(newValue.length > 0 ? { target: { value: newValue } } : { target: { value: null } });

      setValues((current) => current.concat([finalValue]));
    }
    setFinalValue('');
  };

  const saveEdit = (index) => {
    if (
      editingValue &&
      !disallowedNames.some(
        (name) =>
          name.toLocaleLowerCase().replace(/ /g, '') ===
          `${editingValue}`?.toLocaleLowerCase().replace(/ /g, ''),
      )
    ) {
      const newValues = [...values];
      newValues[index] = editingValue;

      const formattedValues =
        type === 'number'
          ? newValues.map((v) => v.replace(/[^\d\.\,]*/g, '').replace(/\,/g, '.'))
          : newValues;

      onChange(
        formattedValues.length > 0
          ? { target: { value: formattedValues } }
          : { target: { value: null } },
      );

      setValues(newValues);
    }
    setEditingIndex(-1);
    setEditingValue('');
  };

  useEffect(() => {
    setDisallowedNames([
      t('new_category'),
      t('new_property'),
      t('new_plant'),
      t('new_area'),
      t('new_sub_area'),
      t('new_line'),
      t('new_machine'),
      t('new_assembly'),
      t('new_equipment'),
      t('new_component'),
      t('new_instrument'),
      t('new_system'),
      t('new_cost_center'),
      t('new_manufacturer'),
      t('new_organization'),
      t('new_department'),
      t('new_interconnection'),
      t('new_user'),
      t('new_user_group'),
      t('new_executor'),
      'email@email.com',
    ]);
  }, [t]);

  return (
    <div className='inputContainer multi'>
      {icon && <div className='inputIconContainer'>{icon}</div>}
      <div className='currentValueContainer'>
        <input
          disabled={disabled}
          value={
            disallowedNames.some(
              (name) =>
                name.toLocaleLowerCase().replace(/ /g, '') ===
                `${finalValue}`?.toLocaleLowerCase().replace(/ /g, ''),
            )
              ? ''
              : finalValue
          }
          placeholder={placeholder}
          style={{ ...style, paddingLeft: icon ? '3rem' : '' }}
          className={`customInput ${disabled ? 'disabled' : ''}`}
          onChange={(e) => {
            if (type === 'number') {
              if (finalValue.includes('.')) {
                const lastChar = e.target.value.slice(-1);
                if (lastChar === ',' || lastChar === '.') {
                  return;
                }
              }
              setFinalValue(e.target.value.replace(/[^\d\.\,]*/g, '').replace(/\,/g, '.'));
            } else {
              setFinalValue(e.target.value);
            }
          }}
          maxLength={maxLength}
          onBlur={onBlur ? onBlur : () => {}}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addOption();
            }
          }}
        />
        <div className='addIcon' onClick={addOption}>
          <PlusCircleOutlined style={{ fontSize: ' 1.5rem', color: '#494949' }} />
        </div>
      </div>

      {values.map((v, index) => (
        <div className='previousValueContainer' key={index}>
          <input
            className={`customInput ${editingIndex === index ? '' : 'disabled'}`}
            disabled={editingIndex !== index}
            value={editingIndex === index ? editingValue : v}
            onChange={(e) => {
              if (editingIndex === index) {
                if (type === 'number') {
                  if (finalValue.includes('.')) {
                    const lastChar = e.target.value.slice(-1);
                    if (lastChar === ',' || lastChar === '.') {
                      return;
                    }
                  }
                  setEditingValue(e.target.value.replace(/[^\d\.\,]*/g, '').replace(/\,/g, '.'));
                } else {
                  setEditingValue(e.target.value);
                }
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && editingIndex === index) {
                e.preventDefault();
                saveEdit(index);
              }
            }}
            onBlur={() => {
              if (editingIndex === index) {
                saveEdit(index);
              }
            }}
          />
          {editingIndex !== index && !disabled && (
            <div
              className='editIcon'
              onClick={() => {
                if (!disabled) {
                  setEditingIndex(index);
                  setEditingValue(v);
                }
              }}
            >
              <EditOutlined
                style={{ fontSize: '1.5rem', color: '#494949', marginRight: '0.5rem' }}
              />
            </div>
          )}
          <div
            className={`removeIcon ${disabled ? 'disabled' : ''}`}
            onClick={() => {
              if (!disabled) {
                const newValue =
                  type === 'number'
                    ? values
                        .filter((c) => c != v)
                        .map((v) => v.replace(/[^\d\.\,]*/g, '').replace(/\,/g, '.'))
                    : values.filter((c) => c != v);

                onChange(
                  newValue.length > 0
                    ? { target: { value: newValue } }
                    : { target: { value: null } },
                );
                setValues((current) => current.filter((c) => c != v));
                if (editingIndex === index) {
                  setEditingIndex(-1);
                  setEditingValue('');
                }
              }
            }}
          >
            <CloseCircleOutlined style={{ fontSize: '1.5rem', color: 'rgb(255, 77, 79)' }} />
          </div>
        </div>
      ))}
    </div>
  );
};

export default MultiInput;
